import React, { useState, useEffect, useRef } from 'react';
import AdUnit from './AdUnit';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  ArrowLeft,
  Link2,
  Check,
  Volume2,
  VolumeX,
  Pause,
  Play,
} from 'lucide-react';

// ── Social icon SVGs (not in this lucide-react version) ───────────────────────
const XLogo: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInLogo: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const FacebookLogo: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// ── Types ─────────────────────────────────────────────────────────────────────
interface Source {
  title: string;
  url?: string;
}

export interface Article {
  id: string;
  date: string;
  category: string;
  categoryColor: string;
  title: string;
  excerpt: string;
  content: string;
  sources?: Source[];
  image?: string; // hero image URL
  publishedAt?: string;
  scheduled_for?: string; // YYYY-MM-DD — article hidden until this date
}

interface ArticleDetailProps {
  article: Article;
  onBack: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
// Always use the canonical article URL so share links work even when the
// component is rendered in a context where window.location hasn't updated yet.
const getCanonicalUrl = (articleId: string) =>
  `https://utxomacro.com/articles/${articleId}`;

const shareLinks = (title: string, articleId: string) => {
  const canonicalUrl = encodeURIComponent(getCanonicalUrl(articleId));
  const shareText = encodeURIComponent(`${title} — via @UTXOMacro`);
  return {
    twitter:  `https://twitter.com/intent/tweet?text=${shareText}&url=${canonicalUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${canonicalUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${canonicalUrl}`,
    canonical: getCanonicalUrl(articleId),
  };
};

// ── Voice picker ─────────────────────────────────────────────────────────────
// Priority list: best neural voices first (Chrome/Edge ship these online)
const VOICE_PRIORITY = [
  // Google neural (Chrome)
  'Google US English',
  'Google UK English Female',
  'Google UK English Male',
  // Microsoft neural (Edge / Chromium)
  'Microsoft Aria Online (Natural) - English (United States)',
  'Microsoft Guy Online (Natural) - English (United States)',
  'Microsoft Jenny Online (Natural) - English (United States)',
  'Microsoft Ana Online (Natural) - English (United States)',
  // Older Microsoft online voices
  'Microsoft Zira Online - English (United States)',
  'Microsoft David Online - English (United States)',
  // macOS enhanced voices (Safari / macOS Chrome)
  'Samantha (Enhanced)',
  'Alex',
];

const pickBestVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null => {
  // 1. Try priority list in order
  for (const name of VOICE_PRIORITY) {
    const v = voices.find(v => v.name === name);
    if (v) return v;
  }
  // 2. Any online/network en-US voice (tends to be neural)
  const onlineEnUS = voices.find(v => v.lang.startsWith('en') && v.localService === false);
  if (onlineEnUS) return onlineEnUS;
  // 3. Any en-US local voice
  const localEnUS = voices.find(v => v.lang === 'en-US');
  if (localEnUS) return localEnUS;
  // 4. Any English voice
  return voices.find(v => v.lang.startsWith('en')) ?? null;
};

const getVoices = (): Promise<SpeechSynthesisVoice[]> =>
  new Promise(resolve => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) { resolve(voices); return; }
    // Voices load asynchronously on first call
    const handler = () => {
      resolve(window.speechSynthesis.getVoices());
      window.speechSynthesis.removeEventListener('voiceschanged', handler);
    };
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    // Fallback timeout — resolve with whatever is available after 1.5s
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 1500);
  });

const stripMarkdown = (md: string): string =>
  md
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/---/g, '. ')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ' ')
    .trim();

// ── Sources list (always visible) ────────────────────────────────────────────
const SourcesList: React.FC<{ sources: Source[] }> = ({ sources }) => {
  if (!sources.length) return null;

  // Truncate long URLs for display while keeping the full href
  const displayTitle = (s: Source) => {
    const t = s.title ?? '';
    // If title looks like a raw URL, truncate it
    if (t.startsWith('http') && t.length > 60) {
      try {
        const u = new URL(t);
        return u.hostname + u.pathname.slice(0, 30) + (u.pathname.length > 30 ? '…' : '');
      } catch { return t.slice(0, 60) + '…'; }
    }
    return t;
  };

  return (
    <div className="mt-14 border-t border-white/5 pt-6 pb-6">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">
        Sources ({sources.length})
      </p>
      <ul className="space-y-2">
        {sources.map((s, i) => (
          <li key={i} className="text-sm text-gray-500 break-words">
            {s.url
              ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#F7931A] transition-colors">{displayTitle(s)}</a>
              : displayTitle(s)}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────
const ArticleDetail: React.FC<ArticleDetailProps> = ({ article, onBack }) => {
  const [copied, setCopied] = useState(false);
  const [ttsSupported] = useState(() => 'speechSynthesis' in window);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsPaused, setTtsPaused] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    return () => { if (ttsSupported) window.speechSynthesis.cancel(); };
  }, [ttsSupported]);

  const canonicalUrl = `https://utxomacro.com/articles/${article.id}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(canonicalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  const startTTS = async () => {
    if (!ttsSupported) return;
    window.speechSynthesis.cancel();
    setTtsPlaying(true); setTtsPaused(false); // show state immediately

    const voices = await getVoices();
    const voice = pickBestVoice(voices);

    const text = `${article.title}. ${stripMarkdown(article.content)}`;
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-US';
    utt.rate = 1.10;  // slightly faster — sounds more natural/conversational
    utt.pitch = 1;
    if (voice) utt.voice = voice;
    utt.onend  = () => { setTtsPlaying(false); setTtsPaused(false); };
    utt.onerror = () => { setTtsPlaying(false); setTtsPaused(false); };
    utteranceRef.current = utt;
    window.speechSynthesis.speak(utt);
  };

  const pauseTTS  = () => { window.speechSynthesis.pause();  setTtsPaused(true); };
  const resumeTTS = () => { window.speechSynthesis.resume(); setTtsPaused(false); };
  const stopTTS   = () => { window.speechSynthesis.cancel(); setTtsPlaying(false); setTtsPaused(false); };

  const shares = shareLinks(article.title, article.id);

  // ── Shared icon-button style ──────────────────────────────────────────────
  const iconBtn = "w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-white hover:border-white/25 transition-all";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* ── Back link ── */}
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={14} />
          Back to Articles
        </button>

        {/* ── Meta ── */}
        <div className="flex items-center gap-3 mb-4">
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider"
            style={{ backgroundColor: `${article.categoryColor}18`, color: article.categoryColor }}
          >
            {article.category}
          </span>
          <span className="text-xs text-gray-500">{article.date}</span>
        </div>

        {/* ── Title ── */}
        <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-3 tracking-tight">
          {article.title}
        </h1>

        {/* ── Byline ── */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-6 h-6 rounded-full bg-[#F7931A] flex items-center justify-center flex-shrink-0">
            <span className="text-black font-bold text-xs">L</span>
          </div>
          <div>
            <span className="text-sm text-gray-300 font-medium">Lance Hudson</span>
            <span className="text-gray-600 text-sm mx-1.5">&middot;</span>
            <span className="text-sm text-gray-500">Founder, UTXOMacro</span>
          </div>
        </div>

        {/* ── Share + Listen toolbar (under title) ── */}
        <div className="flex items-center gap-2 flex-wrap mb-8 pb-8 border-b border-white/5">
          {/* Social share icons */}
          <button onClick={() => handleShare(shares.twitter)} title="Share on X" className={iconBtn}>
            <XLogo size={14} />
          </button>
          <button onClick={() => handleShare(shares.linkedin)} title="Share on LinkedIn"
            className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-[#0A66C2] hover:border-[#0A66C2]/40 transition-all">
            <LinkedInLogo size={14} />
          </button>
          <button onClick={() => handleShare(shares.facebook)} title="Share on Facebook"
            className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-[#1877F2] hover:border-[#1877F2]/40 transition-all">
            <FacebookLogo size={14} />
          </button>
          <button onClick={handleCopy} title={copied ? 'Copied!' : 'Copy link'} className={iconBtn}>
            {copied ? <Check size={14} className="text-green-400" /> : <Link2 size={14} />}
          </button>

          <div className="w-px h-5 bg-white/10 mx-1" />

          {/* Listen / Pause / Resume / Stop */}
          {ttsSupported && !ttsPlaying && (
            <button onClick={startTTS}
              className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium border border-white/10 text-gray-300 hover:text-white hover:border-[#F7931A]/40 hover:bg-[#F7931A]/6 transition-all">
              <Volume2 size={14} /> Listen
            </button>
          )}
          {ttsSupported && ttsPlaying && !ttsPaused && (
            <button onClick={pauseTTS}
              className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium border border-[#F7931A]/40 bg-[#F7931A]/8 text-[#F7931A] transition-all">
              <Pause size={14} /> Pause
            </button>
          )}
          {ttsSupported && ttsPlaying && ttsPaused && (
            <button onClick={resumeTTS}
              className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium border border-[#F7931A]/40 bg-[#F7931A]/8 text-[#F7931A] transition-all">
              <Play size={14} /> Resume
            </button>
          )}
          {ttsSupported && ttsPlaying && (
            <button onClick={stopTTS} title="Stop" className={iconBtn}>
              <VolumeX size={14} />
            </button>
          )}

          {/* Listening status pill */}
          {ttsPlaying && (
            <span className="flex items-center gap-1.5 text-xs text-[#F7931A] ml-1">
              <Volume2 size={12} className="animate-pulse" />
              {ttsPaused ? 'Paused' : 'Listening…'}
            </span>
          )}
        </div>

        {/* ── Hero image with expand ── */}
        {article.image && (
          <div className="mb-10 relative group">
            <div className="rounded-2xl overflow-hidden border border-white/5" style={{ maxHeight: '480px' }}>
              <img
                src={article.image}
                alt={article.title}
                className="w-full object-cover cursor-pointer"
                style={{ maxHeight: '480px' }}
                onClick={() => window.open(article.image, '_blank')}
                title="Click to view full image"
              />
            </div>
            <button
              onClick={() => window.open(article.image, '_blank')}
              className="absolute bottom-3 right-3 bg-black/60 hover:bg-black/80 text-white/70 hover:text-white text-xs px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1.5"
              title="View full image"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
              Expand
            </button>
          </div>
        )}

        {/* ── Excerpt / lede ── */}
        <p className="text-lg text-gray-400 leading-relaxed mb-8 border-l-2 border-[#F7931A]/50 pl-4">
          {article.excerpt}
        </p>

        {/* ── Ad unit — above body (slot 1) ── */}
        <AdUnit slot="REPLACE_WITH_SLOT_ID_1" className="mb-10" />

        {/* ── Body ── */}
        <div className="prose prose-invert prose-lg max-w-none
          prose-headings:font-bold prose-headings:text-white prose-headings:tracking-tight
          prose-h2:text-base prose-h2:mt-8 prose-h2:mb-3
          prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2
          prose-p:text-gray-300 prose-p:leading-8 prose-p:mb-6
          prose-strong:text-white prose-strong:font-bold
          prose-a:text-[#F7931A] prose-a:no-underline hover:prose-a:underline
          prose-hr:border-white/8
          prose-blockquote:border-l-[#F7931A] prose-blockquote:text-gray-400
          prose-code:text-[#F7931A] prose-code:bg-white/5 prose-code:rounded prose-code:px-1
          prose-img:rounded-lg prose-img:w-full prose-img:my-8 prose-img:border prose-img:border-white/10">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              img: ({ src, alt }) => (
                <figure className="my-8">
                  <img
                    src={src}
                    alt={alt ?? ''}
                    className="w-full rounded-lg border border-white/10"
                    loading="lazy"
                  />
                  {alt && (
                    <figcaption className="mt-2 text-center text-xs text-gray-500 font-mono tracking-wide">
                      {alt}
                    </figcaption>
                  )}
                </figure>
              ),
            }}
          >{article.content}</ReactMarkdown>
        </div>

        {/* ── Sources (collapsible) ── */}
        {article.sources && article.sources.length > 0 && (
          <SourcesList sources={article.sources} />
        )}

        {/* ── Ad unit — below body (slot 2) ── */}
        <AdUnit slot="REPLACE_WITH_SLOT_ID_2" className="mt-4 mb-8" />

        {/* ── Bottom back link ── */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> Back to Articles
          </button>
        </div>

      </article>
    </div>
  );
};

export default ArticleDetail;
